import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { DeviceVm } from 'api/management/device.vm';
import { GetDevicesVm } from 'api/management/get-devices.vm';
import { getDevices } from 'api/management/get-devices';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';

const DevicesPage = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<DeviceVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(searchParams.get('warehouse'));
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(searchParams.get('search') || '');
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);

  const fetchDevices = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getDevices(offset || '', selectedWarehouse || '', appliedSearchTerm, {
      success: (response: GetDevicesVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setDevices((prevDevices) => (reset ? response.objects : [...prevDevices, ...response.objects]));
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter dispositivos:', err);
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        const warehouseList = response.objects.map((w) => ({ id: w.id, name: w.name }));
        setWarehouses(warehouseList);

        // Keep selectedWarehouse as null by default to show all warehouses
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    // Always reset devices when filters change (not loading more)
    fetchDevices(null, true);

    // Update URL params
    const params = new URLSearchParams();
    if (selectedWarehouse) params.set('warehouse', selectedWarehouse);
    if (appliedSearchTerm) params.set('search', appliedSearchTerm);
    setSearchParams(params);
  }, [selectedWarehouse, appliedSearchTerm, setSearchParams]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    // Load more - append to existing devices (reset = false)
    fetchDevices(nextOffset, false);
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
  };

  const handleClearFilters = () => {
    // Clear both search term and warehouse
    setSearchTerm('');
    setAppliedSearchTerm('');
    setSelectedWarehouse(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Dispositivos
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Armazém</InputLabel>
              <Select value={selectedWarehouse || ''} label="Armazém" onChange={(e) => setSelectedWarehouse(e.target.value || null)}>
                <MenuItem value="">Todos os Armazéns</MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Procurar por nome"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <Button variant="contained" onClick={handleSearch} sx={{ ml: 1 }} disabled={loading}>
                    <Search />
                  </Button>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!appliedSearchTerm && !selectedWarehouse}
              fullWidth
              sx={{ height: '56px' }}
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Armazém</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id} hover>
                <TableCell>{device.id}</TableCell>
                <TableCell>{device.name}</TableCell>
                <TableCell>{device.warehouseId}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : devices.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais dispositivos para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {selectedWarehouse || appliedSearchTerm
              ? 'Nenhum dispositivo encontrado com os filtros aplicados'
              : 'Nenhum dispositivo disponível'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DevicesPage;
